import { Box, Button, useMediaQuery, useTheme } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { defaultCategory } from '../../pages/home/page';
import { Category } from '../../queries/categoriesQuery';
import { Marker } from '../../queries/markersQuery';
import { filterMarkers } from '../../shared/filterMarkers';
import { isEqual } from '../../shared/utils';
import { Map } from '../map/Map';

interface MainAppProps {
  currentCategory: Category;
  markers?: Marker[];
  categories?: Category[];
}

export const MainApp: React.FC<MainAppProps> = ({ markers, categories, currentCategory }) => {
  const [markersData, setMarkersData] = React.useState<{ data?: Marker[]; category: Category }>({
    data: filterMarkers(currentCategory.id, markers),
    category: currentCategory,
  });
  const { t } = useTranslation();
  const isDefaultCategorySelected = React.useMemo(
    () => isEqual(defaultCategory.value, markersData.category.value),
    [markersData.category.value]
  );
  const theme = useTheme();
  const isSmallMobile = !useMediaQuery(theme.breakpoints.up('sm'));
  const flex = `${isSmallMobile ? '1' : '0'} auto`;

  return (
    <div className="App">
      <Box
        sx={{
          width: '100vw',
          display: 'flex',
          alignItems: 'end',
          flexWrap: 'wrap',
          gap: '16px',
          margin: '16px 0',
        }}
      >
        <Button
          variant="outlined"
          color="primary"
          sx={{
            minWidth: '155px',
            borderColor: isDefaultCategorySelected ? defaultCategory.color : '#000',
            color: isDefaultCategorySelected ? '#fff' : '#000',
            backgroundColor: isDefaultCategorySelected ? defaultCategory.color : 'transparent',
            flex,
            '&:hover': {
              backgroundColor: defaultCategory.color,
              borderColor: defaultCategory.color,
              color: '#fff',
            },
          }}
          onClick={() =>
            setMarkersData({
              data: filterMarkers('all', markers),
              category: defaultCategory,
            })
          }
        >
          {t('seeAll')}
        </Button>
        {categories &&
          categories.map(category => {
            const isCurrentCategory = isEqual(category.value, markersData.category.value);

            return (
              <Button
                variant="outlined"
                sx={{
                  borderColor: isCurrentCategory ? category.color : '#000',
                  color: isCurrentCategory ? '#fff' : '#000',
                  minWidth: '155px',
                  backgroundColor: isCurrentCategory ? category.color : 'transparent',
                  flex,
                  '&:hover': {
                    backgroundColor: category.color,
                    borderColor: category.color,
                    color: '#fff',
                  },
                }}
                key={category.value}
                onClick={() =>
                  setMarkersData({
                    data: filterMarkers(category.id, markers),
                    category: { value: category.value, color: category.color, id: category.id },
                  })
                }
              >
                {category.value}
              </Button>
            );
          })}
      </Box>
      {markersData.data && (
        <Box sx={{ flex: '1' }}>
          <Map markers={markersData.data} currentCategory={markersData.category} />
        </Box>
      )}
    </div>
  );
};
